import type { LucideIcon } from 'lucide-react'

import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Download,
  RefreshCw,
  Users,
} from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts'
import { toast } from 'sonner'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type { Option as MultipleSelectorOption } from '@/components/ui/multiple-selector'
import type { EmployeeLeaveEntitlementReportItem } from '@/utils/api/leave-service/models/employee-leave-entitlement-report-item'
import type { LeaveSummaryReportResponse } from '@/utils/api/leave-service/models/leave-summary-report-response'
import type {
  ListDepartmentsParams,
  ListDirectoratesParams,
} from '@/utils/api/user-service/models'

import SearchSelect from '@/components/search-select'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import MultipleSelector from '@/components/ui/multiple-selector'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isAdminUser } from '@/lib/role-guards'
import { cn } from '@/lib/utils'
import {
  employeeEntitlements,
  useDepartmentPerformance,
  useLeaveTypeGroups,
  useLeaveTypeUsage,
  useListAllLeaveTypes,
  useSummary,
} from '@/utils/api/leave-service'
import {
  useGetAllOrganisations,
  useListDepartments,
  useListDirectorates,
} from '@/utils/api/user-service'

import DashboardEmptyState from '../components/report/DashboardEmptyState'
import { MetricCard } from '../components/report/MetricCard'

type QuarterOption = 'all' | '1' | '2' | '3' | '4'

const QUARTER_OPTIONS: { value: QuarterOption; label: string }[] = [
  { value: 'all', label: 'Full year' },
  { value: '1', label: 'Q1' },
  { value: '2', label: 'Q2' },
  { value: '3', label: 'Q3' },
  { value: '4', label: 'Q4' },
]

const QUARTER_SELECT_OPTIONS = QUARTER_OPTIONS.map(option => ({
  id: option.value,
  value: option.value,
  name: option.label,
}))

const REQUEST_STATUS_TONES: Record<string, string> = {
  approved: 'bg-emerald-500/10 text-emerald-600',
  pending: 'bg-amber-500/10 text-amber-600',
}

const METRIC_CARDS: ReadonlyArray<{
  key: string
  label: string
  helper: string
  icon: LucideIcon
  tone: 'primary' | 'emerald' | 'amber' | 'violet'
  getValue: (summary?: LeaveSummaryReportResponse) => number | undefined
}> = [
  {
    key: 'employees',
    label: 'Total employees',
    helper: 'Active workforce',
    icon: Users,
    tone: 'primary',
    getValue: summary => summary?.totalEmployees,
  },
  {
    key: 'with-leave',
    label: 'Employees with leave',
    helper: 'Recorded this period',
    icon: CheckCircle2,
    tone: 'emerald',
    getValue: summary => summary?.employeesWithLeave,
  },
  {
    key: 'requests',
    label: 'Leave requests',
    helper: 'Approved + pending',
    icon: BarChart3,
    tone: 'amber',
    getValue: summary => (summary?.approvedRequests ?? 0) + (summary?.pendingRequests ?? 0),
  },
  {
    key: 'days',
    label: 'Days taken',
    helper: 'Total leave days',
    icon: CalendarDays,
    tone: 'violet',
    getValue: summary => summary?.totalDaysTaken,
  },
]

function formatNumber(value?: number) {
  if (value === undefined || Number.isNaN(value)) {
    return '—'
  }
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)
}

function formatPercent(value?: number) {
  if (value === undefined || Number.isNaN(value)) {
    return '—'
  }
  return `${Math.round(value)}%`
}

const SAFE_COLOR_OVERRIDES: Record<string, string> = {
  '--background': '#ffffff',
  '--foreground': '#0f172a',
  '--card': '#ffffff',
  '--card-foreground': '#0f172a',
  '--muted': '#f4f4f5',
  '--muted-foreground': '#4b5563',
  '--border': '#d1d5db',
  '--input': '#ffffff',
  '--accent': '#2563eb',
  '--accent-foreground': '#ffffff',
  '--destructive': '#dc2626',
  '--destructive-foreground': '#ffffff',
  '--chart-1': '#22c55e',
  '--chart-2': '#0ea5e9',
  '--chart-3': '#f97316',
  '--chart-4': '#eab308',
  '--chart-5': '#a855f7',
  '--popover': '#ffffff',
  '--popover-foreground': '#0f172a',
  '--primary': '#102749',
  '--primary-foreground': '#ffffff',
  '--secondary': '#A6B595',
  '--secondary-foreground': '#102749',
  '--sidebar': '#f5f5f5',
  '--sidebar-foreground': '#0f172a',
  '--sidebar-primary': '#102749',
  '--sidebar-primary-foreground': '#ffffff',
  '--sidebar-accent': '#d7e3ee',
  '--sidebar-accent-foreground': '#1e3a8b',
  '--sidebar-border': '#e4e8ef',
  '--sidebar-ring': '#102749',
}

function applySafeColorOverrides(): () => void {
  const root = document.documentElement
  if (!root) {
    return () => undefined
  }

  const previousValues = new Map<string, string | null>()
  Object.entries(SAFE_COLOR_OVERRIDES).forEach(([key, value]) => {
    previousValues.set(key, root.style.getPropertyValue(key) || null)
    root.style.setProperty(key, value)
  })

  const removeOklchFromStyles = () => {
    const allElements = document.querySelectorAll('[style*="oklch"]')
    allElements.forEach((element) => {
      const el = element as HTMLElement
      if (el.style.color?.includes('oklch')) {
        el.style.color = ''
      }
      if (el.style.backgroundColor?.includes('oklch')) {
        el.style.backgroundColor = ''
      }
      if (el.style.borderColor?.includes('oklch')) {
        el.style.borderColor = ''
      }
    })
  }

  removeOklchFromStyles()

  return () => {
    Object.entries(SAFE_COLOR_OVERRIDES).forEach(([key]) => {
      const previous = previousValues.get(key)
      if (previous) {
        root.style.setProperty(key, previous)
      }
      else {
        root.style.removeProperty(key)
      }
    })
  }
}

export function LeaveReportsPage() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const [quarter, setQuarter] = useState<QuarterOption>(() => {
    const month = new Date().getMonth()
    const currentQuarter = Math.ceil((month + 1) / 3)
    if (currentQuarter === 1) {
      return 'all'
    }
    return (currentQuarter - 1).toString() as QuarterOption
  })
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [organisationFilter, setOrganisationFilter] = useState('all')
  const [leaveTypeFilter, setLeaveTypeFilter] = useState<string[] | null>(null)
  const [directorateFilter, setDirectorateFilter] = useState('all')
  const [isExporting, setIsExporting] = useState(false)
  const reportRef = useRef<HTMLDivElement | null>(null)
  const entitlementsRef = useRef<HTMLDivElement | null>(null)
  const { user: currentUser } = useAuth()
  const isSuperAdmin = isAdminUser(currentUser)
  const requiresOrganisationSelection = !isSuperAdmin
  const organisationsQuery = useGetAllOrganisations()
  const organisations = useMemo(
    () => organisationsQuery.data?.data ?? [],
    [organisationsQuery.data],
  )
  const effectiveOrganisationFilter = useMemo(() => {
    if (organisationFilter !== 'all') {
      return organisationFilter
    }
    if (isSuperAdmin) {
      return 'all'
    }
    const firstOrganisation = organisations.find(org => typeof org.id === 'number')
    return firstOrganisation?.id?.toString() ?? 'all'
  }, [isSuperAdmin, organisationFilter, organisations])
  const selectedOrganisationId
    = effectiveOrganisationFilter !== 'all' ? Number(effectiveOrganisationFilter) : undefined
  const canRunScopedQueries = !requiresOrganisationSelection || selectedOrganisationId !== undefined

  const leaveTypeQuery = useListAllLeaveTypes(
    { pageable: { page: 0, size: 200 } },
    { query: { staleTime: 60_000 } },
  )
  const leaveTypes = useMemo(
    () => leaveTypeQuery.data?.data?.content ?? [],
    [leaveTypeQuery.data],
  )
  const directorateParams = useMemo<ListDirectoratesParams>(() => ({
    organisationId: selectedOrganisationId,
    page: 0,
    size: 200,
  }), [selectedOrganisationId])
  const directoratesQuery = useListDirectorates(directorateParams, {
    query: {
      staleTime: 60_000,
      enabled: canRunScopedQueries,
    },
  })
  const directorates = useMemo(
    () => directoratesQuery.data?.data?.content ?? [],
    [directoratesQuery.data],
  )
  const directoratesBySelectedOrganisation = useMemo(() => {
    if (!selectedOrganisationId)
      return directorates
    return directorates.filter(d => d.organisationId === selectedOrganisationId)
  }, [directorates, selectedOrganisationId])
  const directorateOptions = useMemo(() => {
    const base: Array<{ id: string; value: string; name: string }> = [
      { id: 'all', value: 'all', name: 'All directorates' },
    ]
    const seen = new Set<string>()
    directoratesBySelectedOrganisation.forEach((directorate) => {
      const id = directorate.id?.toString()
      if (!id || seen.has(id))
        return
      seen.add(id)
      base.push({
        id,
        value: id,
        name: String(directorate.name ?? `Directorate ${id}`),
      })
    })
    return base
  }, [directoratesBySelectedOrganisation])
  const effectiveDirectorateFilter = useMemo(() => {
    if (directorateFilter === 'all') {
      return 'all'
    }
    const isValid = directorateOptions.some(option => option.value === directorateFilter)
    return isValid ? directorateFilter : 'all'
  }, [directorateFilter, directorateOptions])

  const departmentsParams = useMemo<ListDepartmentsParams>(() => ({
    organisationId: selectedOrganisationId,
    directorateId: effectiveDirectorateFilter === 'all' ? undefined : Number(effectiveDirectorateFilter),
    page: 0,
    size: 200,
  }), [effectiveDirectorateFilter, selectedOrganisationId])
  const departmentsQuery = useListDepartments(departmentsParams, {
    query: {
      staleTime: 60_000,
      enabled: canRunScopedQueries,
    },
  })
  const departmentCatalog = useMemo(
    () => departmentsQuery.data?.data?.content ?? [],
    [departmentsQuery.data],
  )

  const leaveTypesBySelectedOrganisation = useMemo(() => {
    if (!selectedOrganisationId)
      return leaveTypes
    return leaveTypes.filter(type => type.organisationId === selectedOrganisationId)
  }, [leaveTypes, selectedOrganisationId])

  const leaveTypeOptions = useMemo<MultipleSelectorOption[]>(() => {
    const seen = new Set<string>()
    const options: MultipleSelectorOption[] = []
    leaveTypesBySelectedOrganisation.forEach((type) => {
      const id = type.id?.toString()
      if (!id || seen.has(id))
        return
      seen.add(id)
      options.push({
        value: id,
        label: type.name ?? type.code ?? `Leave type ${id}`,
      })
    })
    return options
  }, [leaveTypesBySelectedOrganisation])

  const annualLeaveTypeIds = useMemo(() => {
    return leaveTypesBySelectedOrganisation
      .filter((type) => {
        const label = `${type.name ?? ''} ${type.code ?? ''}`.trim().toLowerCase()
        return label.includes('annual')
      })
      .map(type => type.id?.toString())
      .filter((id): id is string => Boolean(id))
  }, [leaveTypesBySelectedOrganisation])
  const effectiveLeaveTypeFilter = useMemo(() => {
    const validLeaveTypeIds = new Set(leaveTypeOptions.map(option => option.value))
    const source = leaveTypeFilter ?? annualLeaveTypeIds
    return source.filter(value => validLeaveTypeIds.has(value))
  }, [annualLeaveTypeIds, leaveTypeFilter, leaveTypeOptions])

  const departmentsByOrganisation = useMemo(() => {
    if (!selectedOrganisationId)
      return departmentCatalog
    return departmentCatalog.filter(dept => dept.organisationId === selectedOrganisationId)
  }, [departmentCatalog, selectedOrganisationId])

  const visibleDepartmentsByDirectorate = useMemo(() => {
    if (effectiveDirectorateFilter === 'all')
      return departmentsByOrganisation
    return departmentsByOrganisation.filter((dept) => {
      const directorateId = dept.directorateId?.toString()
      return directorateId === effectiveDirectorateFilter
    })
  }, [departmentsByOrganisation, effectiveDirectorateFilter])
  const effectiveDepartmentFilter = useMemo(() => {
    if (departmentFilter === 'all') {
      return 'all'
    }
    const isValid = visibleDepartmentsByDirectorate.some(
      dept => dept.id?.toString() === departmentFilter,
    )
    return isValid ? departmentFilter : 'all'
  }, [departmentFilter, visibleDepartmentsByDirectorate])
  const handleOrganisationFilterChange = useCallback((value: string) => {
    setOrganisationFilter(value)
    setDirectorateFilter('all')
    setDepartmentFilter('all')
    setLeaveTypeFilter(null)
  }, [])
  const handleDirectorateFilterChange = useCallback((value: string) => {
    setDirectorateFilter(value)
    setDepartmentFilter('all')
  }, [])

  const quarterParam = quarter === 'all' ? undefined : Number(quarter)
  const departmentIdParam = effectiveDepartmentFilter === 'all' ? undefined : Number(effectiveDepartmentFilter)
  const organisationIdParam = selectedOrganisationId
  const selectedLeaveTypeIds = useMemo(
    () => effectiveLeaveTypeFilter.map(value => Number(value)).filter(value => !Number.isNaN(value)),
    [effectiveLeaveTypeFilter],
  )
  const leaveTypeIdsParam = selectedLeaveTypeIds.length > 0 ? selectedLeaveTypeIds : undefined
  const directorateIdParam = effectiveDirectorateFilter === 'all' ? undefined : Number(effectiveDirectorateFilter)

  const reportParams = useMemo(() => ({
    year,
    quarter: quarterParam,
    organisationId: organisationIdParam,
    departmentId: departmentIdParam,
    leaveTypeIds: leaveTypeIdsParam,
  }), [year, quarterParam, organisationIdParam, departmentIdParam, leaveTypeIdsParam])

  const summaryQuery = useSummary({
    ...reportParams,
    directorateId: directorateIdParam,
  }, {
    query: { enabled: canRunScopedQueries },
  })
  const leaveTypeUsageQuery = useLeaveTypeUsage(reportParams, {
    query: { enabled: canRunScopedQueries },
  })
  const leaveTypeGroupsQuery = useLeaveTypeGroups(reportParams, {
    query: { enabled: canRunScopedQueries },
  })
  const departmentPerformanceQuery = useDepartmentPerformance(reportParams, {
    query: { enabled: canRunScopedQueries },
  })
  const entitlementsQuery = useQuery({
    queryKey: ['leave-reports-employee-entitlements', reportParams],
    enabled: canRunScopedQueries,
    staleTime: 60_000,
    queryFn: () => employeeEntitlements({
      ...reportParams,
    }),
  })

  const summary = summaryQuery.data?.data
  const directorateDepartmentIds = useMemo(() => {
    if (effectiveDirectorateFilter === 'all' || effectiveDepartmentFilter !== 'all') {
      return undefined
    }
    return new Set(
      visibleDepartmentsByDirectorate
        .map(dept => dept.id)
        .filter((id): id is number => typeof id === 'number'),
    )
  }, [effectiveDepartmentFilter, effectiveDirectorateFilter, visibleDepartmentsByDirectorate])
  const dashboardDepartments = useMemo(() => {
    const rows = departmentPerformanceQuery.data?.data?.departments ?? []
    if (!directorateDepartmentIds) {
      return rows
    }
    return rows.filter(row => row.departmentId !== undefined && directorateDepartmentIds.has(row.departmentId))
  }, [departmentPerformanceQuery.data?.data?.departments, directorateDepartmentIds])
  const leaveTypeUsage = useMemo(
    () => leaveTypeUsageQuery.data?.data?.leaveTypes ?? [],
    [leaveTypeUsageQuery.data?.data?.leaveTypes],
  )
  const leaveTypeGroups = useMemo(
    () => leaveTypeGroupsQuery.data?.data?.groups ?? [],
    [leaveTypeGroupsQuery.data?.data?.groups],
  )
  const entitlements = useMemo(() => {
    const employees = entitlementsQuery.data?.data?.employees ?? []
    if (!directorateDepartmentIds) {
      return employees
    }
    return employees.filter(employee => employee.departmentId !== undefined && directorateDepartmentIds.has(employee.departmentId))
  }, [directorateDepartmentIds, entitlementsQuery.data?.data?.employees])

  const computeUsagePercent = (row: EmployeeLeaveEntitlementReportItem) => {
    const totalEntitled = row.totalEntitledDays ?? 0
    const taken = row.totalTakenInPeriod ?? 0
    return totalEntitled > 0 ? Math.min(100, (taken / totalEntitled) * 100) : 0
  }

  const enrichedEntitlements: (EmployeeLeaveEntitlementReportItem & { usage: number })[] = useMemo(
    () => entitlements.map(employee => ({
      ...employee,
      usage: computeUsagePercent(employee),
    })),
    [entitlements],
  )

  const entitlementColumns = useMemo<ColumnConfig<EmployeeLeaveEntitlementReportItem & { usage: number }>[]>(() => [
    {
      key: 'name',
      header: 'Name',
      accessor: row => row.name ?? row.email ?? '—',
      sortable: true,
      width: 220,
    },
    {
      key: 'departmentName',
      header: 'Department',
      accessor: row => row.departmentName ?? '—',
      sortable: true,
    },
    {
      key: 'email',
      header: 'Email',
      accessor: row => row.email ?? '—',
    },
    {
      key: 'totalEntitledDays',
      header: 'Entitled (year)',
      accessor: row => row.totalEntitledDays,
      type: 'number',
      align: 'right',
      sortable: true,
      format: value => formatNumber(value as number),
    },
    {
      key: 'totalTakenInPeriod',
      header: 'Taken (period)',
      accessor: row => row.totalTakenInPeriod,
      type: 'number',
      align: 'right',
      sortable: true,
      format: value => formatNumber(value as number),
    },
    {
      key: 'totalRemainingDays',
      header: 'Remaining (year)',
      accessor: row => row.totalRemainingDays,
      type: 'number',
      align: 'right',
      sortable: true,
      format: value => formatNumber(value as number),
    },
    {
      key: 'usage',
      header: 'Usage',
      accessor: row => row.usage,
      cell: (value) => {
        const percent = Number(value ?? 0)
        return (
          <div className="flex items-center gap-2 w-full">
            <Progress value={percent} className="h-2 flex-1" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {Math.round(percent)}
              %
            </span>
          </div>
        )
      },
      format: value => `${Math.round(Number(value ?? 0))}%`,
    },
  ], [])

  const departmentOptions = useMemo(() => {
    const base = [
      { id: 'all', name: 'All departments', value: 'all' },
    ]
    const seen = new Set<string>()
    visibleDepartmentsByDirectorate.forEach((dept) => {
      const id = dept.id?.toString()
      if (!id || seen.has(id))
        return
      seen.add(id)
      base.push({
        id,
        value: id,
        name: String(dept.name ?? dept.code ?? `Department ${id}`),
      })
    })
    return base
  }, [visibleDepartmentsByDirectorate])

  const organisationOptions = useMemo(() => {
    const base: Array<{ id: string; value: string; name: string }> = []
    if (isSuperAdmin) {
      base.push({ id: 'all', value: 'all', name: 'All organisations' })
    }
    const seen = new Set<string>()
    organisations.forEach((org) => {
      const id = org.id?.toString()
      if (!id || seen.has(id))
        return
      seen.add(id)
      base.push({
        id,
        value: id,
        name: org.name ?? `Organisation ${id}`,
      })
    })
    return base
  }, [organisations, isSuperAdmin])
  const selectedLeaveTypeLabel = useMemo(() => {
    if (effectiveLeaveTypeFilter.length === 0) {
      return 'All leave types'
    }
    const labels = effectiveLeaveTypeFilter
      .map(value => leaveTypeOptions.find(option => option.value === value)?.label)
      .filter((label): label is string => Boolean(label))
    return labels.length > 0 ? labels.join(', ') : 'All leave types'
  }, [effectiveLeaveTypeFilter, leaveTypeOptions])

  const selectedOrganisationLabel = useMemo(() => {
    return organisationOptions.find(option => option.value === effectiveOrganisationFilter)?.name
  }, [effectiveOrganisationFilter, organisationOptions])

  const selectedDirectorateLabel = useMemo(() => {
    return directorateOptions.find(option => option.value === effectiveDirectorateFilter)?.name
  }, [directorateOptions, effectiveDirectorateFilter])

  const showOrganisationAlert
    = requiresOrganisationSelection
      && !organisations.length
      && !organisationsQuery.isPending

  const yearOptions = useMemo(() => {
    return Array.from({ length: 3 }, (_, index) => currentYear - index).map(value => ({
      id: value.toString(),
      value: value.toString(),
      name: value.toString(),
    }))
  }, [currentYear])

  const departmentChartData = useMemo(() => {
    return [...dashboardDepartments]
      .map(dept => ({
        name: dept.departmentCode ?? `Dept ${dept.departmentId ?? '—'}`,
        participation: (dept.participationRate ?? 0) * 100,
        totalDays: dept.totalDaysTaken ?? 0,
      }))
      .sort((a, b) => b.participation - a.participation)
  }, [dashboardDepartments])

  // const topDepartments = useMemo(() => departmentChartData.slice(0, 4), [departmentChartData])

  const leaveTypeChartData = useMemo(() => {
    return [...leaveTypeUsage]
      .map(type => ({
        name: type.leaveTypeName ?? type.leaveTypeCode ?? 'Misc',
        requests: type.totalRequests ?? 0,
      }))
      .sort((a, b) => b.requests - a.requests)
      .slice(0, 6)
  }, [leaveTypeUsage])

  const leaveGroupSummary = useMemo(() => {
    return [...leaveTypeGroups]
      .filter(group => (group.totalDaysTaken ?? 0) > 0)
      .map(group => ({
        label: group.groupName ?? group.groupKey ?? 'Group',
        days: group.totalDaysTaken ?? 0,
        requests: group.totalRequests ?? 0,
      }))
      .sort((a, b) => b.days - a.days)
      .slice(0, 5)
  }, [leaveTypeGroups])

  const statusRows = [
    { label: 'Approved', key: 'approved', value: summary?.approvedRequests },
    { label: 'Pending', key: 'pending', value: summary?.pendingRequests },
  ]

  const totalRequests
    = statusRows.reduce((sum, row) => sum + (row.value ?? 0), 0)
  const requestsFromSelectedLeaveTypes = leaveTypeUsage.reduce(
    (sum, leaveType) => sum + (leaveType.totalRequests ?? 0),
    0,
  )
  const daysFromSelectedLeaveTypes = leaveTypeUsage.reduce(
    (sum, leaveType) => sum + (leaveType.totalDaysTaken ?? 0),
    0,
  )
  const hasSelectedLeaveTypes = selectedLeaveTypeIds.length > 0
  const isDashboardPending = summaryQuery.isPending
    || leaveTypeUsageQuery.isPending
    || leaveTypeGroupsQuery.isPending
    || departmentPerformanceQuery.isPending
  const hasError = summaryQuery.isError
    || leaveTypeUsageQuery.isError
    || leaveTypeGroupsQuery.isError
    || departmentPerformanceQuery.isError
    || entitlementsQuery.isError
  const hasReportData = Boolean(summary)
    || leaveTypeUsage.length > 0
    || leaveTypeGroups.length > 0
    || dashboardDepartments.length > 0
  const quarterLabel = QUARTER_OPTIONS.find(option => option.value === quarter)?.label ?? 'Full year'

  const handleRefresh = () => {
    void summaryQuery.refetch()
    void leaveTypeUsageQuery.refetch()
    void leaveTypeGroupsQuery.refetch()
    void departmentPerformanceQuery.refetch()
    void entitlementsQuery.refetch()
  }

  const handleExport = useCallback(async () => {
    if (!hasReportData) {
      toast('No report data yet. Apply filters to refresh.', { duration: 3000 })
      return
    }

    if (!reportRef.current) {
      toast.error('Unable to prepare report for export.')
      return
    }

    setIsExporting(true)
    const rootElement = document.documentElement
    const previousExportFlag = rootElement.getAttribute('data-exporting-report')
    rootElement.setAttribute('data-exporting-report', 'true')
    let restoreColors: (() => void) | undefined
    try {
      restoreColors = applySafeColorOverrides()
      const html2canvas = (await import('html2canvas')).default
      const baseCanvasOptions = {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
      }

      const baseOnClone = (clonedDocument: Document) => {
        const noExportElements = clonedDocument.querySelectorAll('[data-no-export="true"]')
        noExportElements.forEach((element) => {
          const el = element as HTMLElement
          el.style.display = 'none'
        })

        const styleElement = clonedDocument.createElement('style')
        styleElement.textContent = `
          * {
            color: inherit !important;
            background-color: transparent !important;
            border-color: currentColor !important;
            fill: currentColor !important;
            stroke: currentColor !important;
          }
          svg { fill: black !important; }
          text { fill: black !important; }
        `
        clonedDocument.head.appendChild(styleElement)

        const allElements = clonedDocument.querySelectorAll('[style*="oklch"]')
        allElements.forEach((element) => {
          const el = element as HTMLElement
          el.removeAttribute('style')
        })

        const svgElements = clonedDocument.querySelectorAll('[fill*="oklch"], [stroke*="oklch"]')
        svgElements.forEach((element) => {
          element.removeAttribute('fill')
          element.removeAttribute('stroke')
        })
      }

      const buildOnClone = (extra?: (clonedDocument: Document) => void) => (clonedDocument: Document) => {
        baseOnClone(clonedDocument)
        extra?.(clonedDocument)
      }

      const hideEntitlementsSection = (clonedDocument: Document) => {
        const entitlementsCard = clonedDocument.querySelector('[data-entitlements-section="true"]') as HTMLElement | null
        if (entitlementsCard) {
          entitlementsCard.style.display = 'none'
        }
      }

      const canvases: HTMLCanvasElement[] = []
      const firstCanvas = await html2canvas(reportRef.current, {
        ...baseCanvasOptions,
        onclone: buildOnClone(hideEntitlementsSection),
      })
      canvases.push(firstCanvas)

      if (entitlementsRef.current) {
        const entitlementsCanvas = await html2canvas(entitlementsRef.current, {
          ...baseCanvasOptions,
          onclone: buildOnClone(),
        })
        canvases.push(entitlementsCanvas)
      }

      const { jsPDF } = await import('jspdf')
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      let isFirstCanvas = true

      const addCanvasPages = (canvas: HTMLCanvasElement) => {
        const imgData = canvas.toDataURL('image/png')
        const imgProps = pdf.getImageProperties(imgData)
        const imgHeight = (imgProps.height * pageWidth) / imgProps.width
        let heightLeft = imgHeight
        let position = 0

        if (isFirstCanvas) {
          pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight)
          heightLeft -= pageHeight
          isFirstCanvas = false
        }
        else {
          pdf.addPage()
          pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight)
          heightLeft -= pageHeight
        }

        while (heightLeft > 0) {
          position -= pageHeight
          pdf.addPage()
          pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight)
          heightLeft -= pageHeight
        }
      }

      canvases.forEach(addCanvasPages)

      const quarterSlug = quarterParam ? `Q${quarterParam}` : 'full-year'
      pdf.save(`leave-report-${year}-${quarterSlug}.pdf`)
      toast.success('Report exported.')
    }
    catch (error) {
      console.error('Failed to export report', error)
      toast.error('Report export failed. Try again.')
    }
    finally {
      if (previousExportFlag !== null) {
        rootElement.setAttribute('data-exporting-report', previousExportFlag)
      }
      else {
        rootElement.removeAttribute('data-exporting-report')
      }
      restoreColors?.()
      setIsExporting(false)
    }
  }, [hasReportData, quarterParam, year])

  return (
    <div className="flex flex-col gap-6 p-4" ref={reportRef}>
      <section className="relative">
        <div className="absolute inset-0 bg-grid-white/5" />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold leading-tight text-foreground">
              Leave reports & insights
            </h1>
            <p className="text-sm text-muted-foreground">
              Review participation, requests, and entitlement health for the selected window.
            </p>
            <p className="text-xs text-muted-foreground">
              Showing:
              {' '}
              {quarterLabel}
              ,
              {' '}
              {year}
              {effectiveDepartmentFilter !== 'all'
                ? ` · ${departmentOptions.find(option => option.value === effectiveDepartmentFilter)?.name ?? ''}`
                : ''}
              {effectiveOrganisationFilter && effectiveOrganisationFilter !== 'all'
                ? ` · ${selectedOrganisationLabel ?? 'Organisation'}`
                : ''}
              {effectiveDirectorateFilter !== 'all'
                ? ` · ${selectedDirectorateLabel ?? 'Directorate'}`
                : ''}
              {effectiveLeaveTypeFilter.length > 0
                ? ` · ${selectedLeaveTypeLabel ?? 'Leave type'}`
                : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 print:hidden" data-no-export="true">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              className="gap-2 bg-background/60 backdrop-blur-sm hover:bg-background/80"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 bg-background/60 backdrop-blur-sm hover:bg-background/80"
              onClick={handleExport}
              disabled={!hasReportData || isExporting}
              aria-busy={isExporting}
            >
              <Download className="h-4 w-4" />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>
      </section>

      <Card className="border-primary/20 shadow-sm print:hidden" data-no-export="true">
        <CardHeader className="gap-3 pb-3">
          <div className="space-y-1">
            <CardTitle className="text-base">Report filters</CardTitle>
            <CardDescription className="text-sm">
              Slice the data by year, quarter, and department to uncover trends.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <SearchSelect
            label="Organisation"
            placeholder={requiresOrganisationSelection ? 'Select organisation' : 'All organisations'}
            options={organisationOptions}
            value={effectiveOrganisationFilter}
            onValueChange={handleOrganisationFilterChange}
            isLoading={organisationsQuery.isPending}
            disabled={organisationsQuery.isPending || organisationsQuery.isError}
          />
          <SearchSelect
            label="Directorate"
            placeholder="All directorates"
            options={directorateOptions}
            value={effectiveDirectorateFilter}
            onValueChange={handleDirectorateFilterChange}
            isLoading={directoratesQuery.isPending}
            disabled={directoratesQuery.isPending || directoratesQuery.isError}
          />
          <SearchSelect
            label="Department"
            placeholder="All departments"
            options={departmentOptions}
            value={effectiveDepartmentFilter}
            onValueChange={setDepartmentFilter}
          />
          <div className="space-y-2">
            <p className="text-sm font-medium">Leave type</p>
            <MultipleSelector
              placeholder="All leave types"
              options={leaveTypeOptions}
              value={leaveTypeOptions.filter(option => effectiveLeaveTypeFilter.includes(option.value))}
              onChange={options => setLeaveTypeFilter(options.map(option => option.value))}
              disabled={leaveTypeQuery.isPending || leaveTypeQuery.isError}
              hidePlaceholderWhenSelected
              emptyIndicator={(
                <p className="px-2 py-2 text-sm text-muted-foreground">No leave type found.</p>
              )}
            />
          </div>
          <SearchSelect
            label="Year"
            options={yearOptions}
            value={year.toString()}
            onValueChange={value => setYear(Number(value))}
          />
          <SearchSelect
            label="Quarter"
            placeholder="Select quarter"
            options={QUARTER_SELECT_OPTIONS}
            value={quarter}
            onValueChange={value => setQuarter(value as QuarterOption)}
          />
        </CardContent>
      </Card>
      {showOrganisationAlert && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Organisations not available</AlertTitle>
          <AlertDescription>
            We could not load your organisations. Please refresh the page or contact support to continue.
          </AlertDescription>
        </Alert>
      )}

      {hasError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unable to load reports</AlertTitle>
          <AlertDescription>
            Something went wrong while fetching the analytics. Refresh or adjust the filters and try again.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {METRIC_CARDS.map(metric => (
          <MetricCard
            key={metric.key}
            icon={metric.icon}
            label={metric.label}
            helper={metric.helper}
            tone={metric.tone}
            value={formatNumber(
              metric.key === 'requests' && hasSelectedLeaveTypes
                ? requestsFromSelectedLeaveTypes
                : metric.key === 'days' && hasSelectedLeaveTypes
                  ? daysFromSelectedLeaveTypes
                  : metric.getValue(summary),
            )}
            isLoading={isDashboardPending}
          />
        ))}
      </div>

      <div
        ref={entitlementsRef}
        data-entitlements-section="true"
        className="leave-entitlements-section"
      >
        <Card className="border-primary/15">
          <CardHeader>
            <div className="space-y-1">
              <CardTitle>Entitlements spotlight</CardTitle>
              <CardDescription className="text-sm">
                Employees by days taken inside the selected window.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {entitlementsQuery.isPending
              ? (
                <Skeleton className="h-40 w-full rounded-xl" />
              )
              : (
                <EnhancedDataTable
                  data={enrichedEntitlements}
                  columns={entitlementColumns}
                  loading={entitlementsQuery.isPending}
                  emptyStateMessage="Entitlement details not available."
                  enableColumnVisibility
                  enableExport
                  enablePagination
                  exportOptions={{
                    filename: `leave-entitlements-${year}-Q${quarterParam ?? 'full'}`,
                  }}
                  pageSize={10}
                  pageSizeOptions={[5, 10, 20]}
                  initialColumnVisibility={{ email: false }}
                  className="overflow-hidden rounded-lg border border-muted/30"
                />
              )}
          </CardContent>
        </Card>
      </div>

      {/* <div className="grid gap-4 lg:grid-cols-[1.5fr_1fr]"> */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-primary/15">
            <CardHeader className="pb-0">
              <div className="space-y-1">
                <CardTitle>Leave type usage</CardTitle>
                <CardDescription className="text-sm">
                  Requests per leave type for the selected filters.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {isDashboardPending
                ? (
                  <Skeleton className="h-[240px] w-full rounded-xl" />
                )
                : leaveTypeChartData.length === 0
                  ? (
                    <DashboardEmptyState message="Leave type usage is not yet available." />
                  )
                  : (
                    <ChartContainer
                      className="h-[260px] w-full rounded-xl border border-primary/15 bg-primary/5"
                      config={{
                        requests: {
                          label: 'Requests',
                          color: 'var(--color-chart-1)',
                        },
                      }}
                    >
                      <BarChart
                        data={leaveTypeChartData}
                        margin={{ top: 16, right: 16, left: -6, bottom: 40 }}
                      >
                        <CartesianGrid vertical={false} strokeDasharray="4 8" />
                        <XAxis dataKey="name" tickLine={false} axisLine={false} />
                        <YAxis tickLine={false} axisLine={false} />
                        <ChartTooltip
                          cursor={false}
                          content={(
                            <ChartTooltipContent
                              formatter={value => (
                                <div className="flex items-center justify-between gap-4">
                                  <span className="text-muted-foreground">Requests</span>
                                  <span className="font-semibold">
                                    {formatNumber(Number(value))}
                                  </span>
                                </div>
                              )}
                            />
                          )}
                        />
                        <ChartLegend content={<ChartLegendContent className="pt-2" />} />
                        <Bar
                          dataKey="requests"
                          fill="var(--color-chart-1)"
                          barSize={28}
                          radius={[6, 6, 0, 0]}
                        >
                          <LabelList
                            dataKey="requests"
                            position="top"
                            // formatter={(value: any) => formatPercent(Number(value))}
                          />
                        </Bar>
                      </BarChart>
                    </ChartContainer>
                  )}
            </CardContent>
          </Card>
        </div>
        <div className="lg:col-span-1 space-y-4">
          <div className="space-y-1">
            <Card className="border-primary/15">
              <CardHeader>
                <div className="space-y-1">
                  <CardTitle>Request status</CardTitle>
                  <CardDescription className="text-sm">
                    Approved and pending leave submissions.
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {isDashboardPending
                  ? (
                    <Skeleton className="h-36 w-full rounded-xl" />
                  )
                  : (
                    <div className="space-y-3">
                      {statusRows.map((status) => {
                        const percent = totalRequests
                          ? Math.round(((status.value ?? 0) / totalRequests) * 100)
                          : 0
                        return (
                          <div
                            key={status.key}
                            className="space-y-1"
                          >
                            <div className="flex items-center justify-between text-sm font-medium">
                              <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', REQUEST_STATUS_TONES[status.key])}>
                                {status.label}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatNumber(status.value)}
                              </span>
                            </div>
                            <Progress value={Math.min(100, percent)} />
                          </div>
                        )
                      })}
                    </div>
                  )}

              </CardContent>
            </Card>
          </div>
          <div className="space-y-1">
            {/* <div className="space-y-4 border-t border-muted/20 pt-4"> */}
            <Card className="border-primary/15">
              <CardHeader>
                <CardTitle className="text-sm">Leave type groups</CardTitle>
                <CardDescription className="text-sm">
                  Consumption by grouped leave buckets.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isDashboardPending
                  ? (
                    <Skeleton className="h-48 w-full rounded-xl" />
                  )
                  : leaveGroupSummary.length === 0
                    ? (
                      <DashboardEmptyState message="No grouped leave data available." />
                    )
                    : (
                      <div>
                        {leaveGroupSummary.map(group => (
                          <div
                            key={group.label}
                            className="space-y-1 rounded-lg border border-dashed border-muted/40 bg-muted/5 py-1.5"
                          >
                            <div className="flex items-center justify-between text-sm font-semibold">
                              <span>{group.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatNumber(group.days)}
                                {' '}
                                days taken
                              </span>
                            </div>
                            <Progress value={Math.min(100, (group.days / (summary?.totalDaysTaken ?? 1)) * 100)} />
                            {/* <p className="text-xs text-muted-foreground">
                              {formatNumber(group.requests)}
                              {' '}
                              requests
                            </p> */}
                          </div>
                        ))}
                      </div>
                    )}
              </CardContent>
            </Card>
          </div>
        </div>

      </div>

      <Card className="border-primary/15">
        <CardHeader className="pb-0">
          <div className="space-y-1">
            <CardTitle>Department participation</CardTitle>
            <CardDescription className="text-sm">
              Participation rates and days consumed per department for the selected filters.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 pt-4">
          {isDashboardPending
            ? (
              <Skeleton className="h-[280px] w-full rounded-xl" />
            )
            : departmentChartData.length === 0
              ? (
                <DashboardEmptyState message="No department performance data." />
              )
              : (
                <div className="space-y-4">
                  <ChartContainer
                    className="leave-entitlements-chart h-[260px] w-full rounded-xl border border-emerald-500/20 bg-emerald-500/5"
                    config={{
                      participation: {
                        label: 'Participation %',
                        color: 'var(--color-chart-1)',
                      },
                    }}
                  >
                    <BarChart
                      data={departmentChartData}
                      margin={{ top: 16, right: 20, left: -8, bottom: 40 }}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="4 8" />
                      <XAxis dataKey="name" tickLine={false} axisLine={false} />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={value => `${Math.round(value)}%`}
                      />
                      <ChartTooltip
                        cursor={false}
                        content={(
                          <ChartTooltipContent
                            formatter={value => (
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-muted-foreground">Participation</span>
                                <span className="font-semibold">
                                  {formatPercent(Number(value))}
                                </span>
                              </div>
                            )}
                          />
                        )}
                      />
                      <ChartLegend content={<ChartLegendContent className="pt-2" />} />
                      <Bar
                        dataKey="participation"
                        fill="var(--color-chart-1)"
                        radius={[6, 6, 0, 0]}
                        barSize={30}
                      >
                        <LabelList
                          dataKey="participation"
                          position="top"
                          formatter={(value: any) => formatPercent(Number(value))}
                        />
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                  {/* <div className="grid gap-3">
                    {topDepartments.map(dept => (
                      <div key={dept.name} className="rounded-lg border border-dashed border-secondary/30 bg-secondary/5 p-3">
                        <div className="flex items-center justify-between text-sm font-medium">
                          <span>{dept.name}</span>
                          <span className="text-xs text-muted-foreground">{formatPercent(dept.participation)}</span>
                        </div>
                        <Progress value={Math.min(100, dept.participation)} className="mt-2" />
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatNumber(dept.totalDays)}
                          {' '}
                          days taken
                        </p>
                      </div>
                    ))}
                  </div> */}
                </div>
              )}
        </CardContent>
      </Card>
    </div>
  )
}

export default LeaveReportsPage
